import React from 'react';
import './SalaryTable.css';

interface Company {
  id: string;
  name: string;
  type: string;
  aum: number;
  salary: number;
  bonus: number;
  carry: number;
  checked: boolean;
  grade: string;
}

interface SalaryTableProps {
  companies: Company[];
  onFilteredCountChange?: (count: number) => void;
}

const SalaryTable: React.FC<SalaryTableProps> = ({ companies, onFilteredCountChange }) => {
  const [selectedFilter, setSelectedFilter] = React.useState<string>('all');
  const [selectedAumFilter, setSelectedAumFilter] = React.useState<string>('all');
  const [selectedDataType, setSelectedDataType] = React.useState<string>('salary');

  // Фильтруем компании по выбранным фильтрам
  const getFilteredCompanies = () => {
    let filtered = companies;
    
    // Фильтр по типу
    switch (selectedFilter) {
      case 'pe':
        filtered = filtered.filter(company => company.type === 'PE');
        break;
      case 'fo':
        filtered = filtered.filter(company => company.type === 'Family Office');
        break;
      case 'all':
      default:
        break;
    }
    
    // Фильтр по AUM
    switch (selectedAumFilter) {
      case 'large':
        filtered = filtered.filter(company => company.aum >= 5);
        break;
      case 'medium':
        filtered = filtered.filter(company => company.aum >= 3 && company.aum < 5);
        break;
      case 'small':
        filtered = filtered.filter(company => company.aum >= 1 && company.aum < 3);
        break;
      case 'tiny':
        filtered = filtered.filter(company => company.aum < 1);
        break;
      case 'all':
      default:
        break;
    }
    
    return filtered;
  };

  const filteredCompanies = getFilteredCompanies();

  // Уведомляем родительский компонент об изменении количества отфильтрованных компаний
  React.useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(filteredCompanies.length);
    }
  }, [filteredCompanies.length, onFilteredCountChange]);

  // Создаем уровни на основе AUM
  const levels = [
    {
      name: 'P1',
      companies: companies.filter(c => c.aum >= 15),
      description: 'Senior Level (AUM 15B+)'
    }
  ];

  const calculatePercentile = (data: number[], percentile: number): number => {
    if (data.length === 0) return 0;
    const sorted = [...data].sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)];
  };

  const formatValue = (value: number): string => {
    if (selectedDataType === 'bonus') {
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value) + '%';
    } else {
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value) + ' ₽';
    }
  };

  return (
    <div className="salary-table-container">
      <div className="salary-table-header">
        <h3 className="salary-table-title">
          {selectedDataType === 'salary' ? 'Базовая заработная плата' : 'Переменная компенсация'}
        </h3>
        <p className="salary-table-subtitle">
          Представленная информация сформирована на основе данных, полученных в ходе полевых исследований команды аналитиков datacircle и регрессионных моделей ИИ-продукта datacircle
        </p>
      </div>
      
      <div className="salary-table-filters">
        <div className="filter-select-container">
          <select 
            className="filter-select"
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
          >
            <option value="all">PE&FO (Все компании)</option>
            <option value="pe">PE (только Private Equity)</option>
            <option value="fo">FO (только Family Office)</option>
          </select>
          <select 
            className="filter-select"
            value={selectedAumFilter}
            onChange={(e) => setSelectedAumFilter(e.target.value)}
          >
            <option value="all">Все по AUM $</option>
                    <option value="large">AUM &gt;= 5млрд$</option>
        <option value="medium">AUM 3-5млрд$</option>
        <option value="small">AUM 1-3млрд$</option>
        <option value="tiny">AUM &lt; 1млрд$</option>
          </select>
          <select 
            className="filter-select"
            value={selectedDataType}
            onChange={(e) => setSelectedDataType(e.target.value)}
          >
            <option value="salary">Базовая заработная плата</option>
            <option value="bonus">Переменная компенсация</option>
          </select>
        </div>
        <div style={{
          textAlign: 'left',
          marginTop: '12px',
          fontSize: '14px',
          color: '#666',
          fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif"
        }}>
          Выбрано компаний: <strong>{filteredCompanies.length}</strong>
        </div>
      </div>
      
      <div className="salary-table-wrapper">
        <table className="salary-table">
          <thead>
            <tr>
              <th>Уровень</th>
              <th>
                <div className="header-with-tooltip">
                  <span>Надежность</span>
                  <div 
                    className="info-icon"
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip') as HTMLElement;
                      if (tooltip) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        tooltip.style.left = (rect.left + rect.width / 2) + 'px';
                        tooltip.style.top = (rect.top - 80) + 'px';
                        tooltip.style.transform = 'translateX(-50%)';
                      }
                    }}
                  >
                    <div className="info-icon-circle">i</div>
                    <div className="tooltip">
                      Показатель надежности данных отражает степень уверенности datacircle в данных, основанную на размере выборки и способе получения данных.
                    </div>
                  </div>
                </div>
              </th>
              <th>10%</th>
              <th>25%</th>
              <th>40%</th>
              <th>50%</th>
              <th>60%</th>
              <th>75%</th>
              <th>90%</th>
            </tr>
          </thead>
          <tbody>
            {levels.map((level, index) => {
              // Используем отфильтрованные компании для расчета процентилей
              const allData = filteredCompanies.map(c => selectedDataType === 'salary' ? c.salary : c.bonus);
              const hasData = allData.length > 0;
              
              return (
                <tr key={level.name} className="salary-table-row">
                  <td className="level-cell">
                    <div className="level-info">
                      <span className="level-name">Senior</span>
                    </div>
                  </td>
                  <td className="consistency-cell">
                    <div className="consistency-indicator">
                      <div className="consistency-dot"></div>
                      <span style={{ fontWeight: 'bold' }}>Высокая</span>
                    </div>
                  </td>
                  <td className="percentile-cell">
                    {hasData ? formatValue(calculatePercentile(allData, 10)) : '-'}
                  </td>
                  <td className="percentile-cell">
                    {hasData ? formatValue(calculatePercentile(allData, 25)) : '-'}
                  </td>
                  <td className="percentile-cell">
                    {hasData ? formatValue(calculatePercentile(allData, 40)) : '-'}
                  </td>
                  <td className="percentile-cell">
                    {hasData ? formatValue(calculatePercentile(allData, 50)) : '-'}
                  </td>
                  <td className="percentile-cell">
                    {hasData ? formatValue(calculatePercentile(allData, 60)) : '-'}
                  </td>
                  <td className="percentile-cell">
                    {hasData ? formatValue(calculatePercentile(allData, 75)) : '-'}
                  </td>
                  <td className="percentile-cell">
                    {hasData ? formatValue(calculatePercentile(allData, 90)) : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalaryTable; 